// src/layouts/DashboardLayout.tsx

import {
  useEffect,
  useState,
} from 'react';

import {
  Outlet,
} from 'react-router-dom';

import Sidebar from '../features/dashboard/components/Sidebar';
import Header from '../features/dashboard/components/Header';

import {
  DashboardDateProvider,
} from '../features/dashboard/context/DashboardDateContext';

/* ============================================================================
   CONFIGURATION
   ============================================================================ */

/*
 * Reference desktop viewport.
 *
 * Your original 100% screenshot is approximately 1365px wide.
 */
const DESIGN_WIDTH = 1365;

/*
 * Minimum scale.
 *
 * 125%:
 *     1092 / 1365 = 0.80
 *
 * 150%:
 *      910 / 1365 = 0.667
 */
const MIN_SCALE = 0.60;

/* ============================================================================
   SCALE
   ============================================================================ */

const getDashboardScale = (
  viewportWidth: number,
): number => {
  if (
    !Number.isFinite(viewportWidth) ||
    viewportWidth <= 0
  ) {
    return 1;
  }

  if (
    viewportWidth >= DESIGN_WIDTH
  ) {
    return 1;
  }

  const scale =
    viewportWidth /
    DESIGN_WIDTH;

  return Math.max(
    MIN_SCALE,
    Math.min(
      1,
      scale,
    ),
  );
};

/* ============================================================================
   COMPONENT
   ============================================================================ */

const DashboardLayout = () => {
  const [
    expanded,
    setExpanded,
  ] = useState(true);

  const [
    dashboardScale,
    setDashboardScale,
  ] = useState(1);

  /* ==========================================================================
     DETECT BROWSER ZOOM
     ========================================================================== */

  useEffect(() => {
    const updateScale = () => {
      /*
       * document.documentElement.clientWidth gives
       * the current CSS viewport width.
       *
       * Browser zoom changes this value.
       */
      const viewportWidth =
        document.documentElement.clientWidth;

      const scale =
        getDashboardScale(
          viewportWidth,
        );

      setDashboardScale(scale);

      /*
       * Add a class when the dashboard is being
       * scaled because of browser zoom.
       *
       * EmployeeDashboard uses this class to prevent
       * its normal responsive rules from stacking
       * the charts.
       */
      document.documentElement.classList.toggle(
        'dashboard-browser-scaled',
        scale < 0.999,
      );
    };

    updateScale();

    window.addEventListener(
      'resize',
      updateScale,
    );

    window.visualViewport?.addEventListener(
      'resize',
      updateScale,
    );

    return () => {
      window.removeEventListener(
        'resize',
        updateScale,
      );

      window.visualViewport?.removeEventListener(
        'resize',
        updateScale,
      );

      document.documentElement.classList.remove(
        'dashboard-browser-scaled',
      );
    };
  }, []);

  /* ==========================================================================
     SIDEBAR
     ========================================================================== */

  const sidebarWidth =
    expanded
      ? '220px'
      : '78px';

  /* ==========================================================================
     SCALED MAIN WIDTH
     ========================================================================== */

  /*
   * IMPORTANT:
   *
   * The scaled content needs additional CSS width.
   *
   * Example:
   *
   * 125% browser
   * scale = 0.80
   *
   * width = 125%
   *
   * 125% × 0.80 = 100%
   *
   * The difference from the previous implementation is that
   * the sidebar is NOT included in this calculation.
   *
   * Only the main dashboard area is scaled.
   */
  const scaledMainWidth =
    `${100 / dashboardScale}%`;

  return (
    <DashboardDateProvider>
      {/* ====================================================================
          PAGE
          ==================================================================== */}

      <div
        className="
          relative
          min-h-screen
          w-full
          min-w-0
          overflow-hidden
          bg-[#F4F7FB]
        "
      >
        {/* ==================================================================
            SIDEBAR
            ================================================================== */}

        <Sidebar
          expanded={expanded}
          setExpanded={setExpanded}
        />

        {/* ==================================================================
            MAIN VIEWPORT

            This remains exactly inside the browser viewport.
            ================================================================== */}

        <div
          className="
            relative
            min-h-screen
            min-w-0
            overflow-hidden
            transition-[margin-left]
            duration-300
            ease-in-out
          "
          style={{
            marginLeft:
              sidebarWidth,
          }}
        >
          {/* ================================================================
              MAIN DESIGN CANVAS

              The canvas becomes wider in CSS and then gets scaled down.

              This preserves the 100% desktop layout instead of allowing
              the 125%/150% browser viewport to trigger the one-column
              dashboard layout.
              ================================================================ */}

          <div
            className="
              dashboard-scale-canvas
              min-h-screen
              min-w-0
              origin-top-left
            "
            style={{
              width:
                scaledMainWidth,

              /*
               * Use transform instead of CSS zoom.

               * transform gives predictable visual scaling.
               */
              transform:
                `scale(${dashboardScale})`,

              /*
               * Keep transform origin at the top-left.
               */
              transformOrigin:
                'top left',
            }}
          >
            {/* ==============================================================
                HEADER
                ============================================================== */}

            <div
              className="
                w-full
                min-w-0
                max-w-full
              "
            >
              <Header />
            </div>

            {/* ==============================================================
                MAIN CONTENT
                ============================================================== */}

            <main
              className="
                box-border
                w-full
                min-w-0
                max-w-full
                overflow-hidden
                p-5
              "
            >
              <div
                className="
                  w-full
                  min-w-0
                  max-w-full
                  overflow-hidden
                "
              >
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </div>
    </DashboardDateProvider>
  );
};

export default DashboardLayout;